import React, { useState, useMemo } from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Autocomplete,
} from '@mui/material';
import {
  useDatastoreSchemas,
  DatastoreEntity,
} from '../../data/canvas/useDatastoreSchemas';
import { useAccount } from '../../auth';

interface DatastoreEntitySelectorProps {
  accountId: string;
  datastoreId: string;
  value?: string; // Selected entity name
  onChange: (entityName: string, entity: DatastoreEntity | undefined) => void;
  label?: string;
  required?: boolean;
}

export function DatastoreEntitySelector({
  accountId,
  datastoreId,
  value,
  onChange,
  label = 'Select Table/View',
  required = false,
}: DatastoreEntitySelectorProps) {
  const [searchTerm, setSearchTerm] = useState('');

  // Get actual account UUID from context (accountId prop might be account name)
  const { currentAccount } = useAccount();
  const actualAccountId = currentAccount?.id || accountId;

  const {
    data: metadata,
    isLoading,
    error,
  } = useDatastoreSchemas(
    actualAccountId,
    datastoreId,
    !!actualAccountId && !!datastoreId,
  );

  // Filter and sort entities
  const filteredEntities = useMemo(() => {
    if (!metadata?.entities) return [];

    let entities = metadata.entities;

    // Apply search filter
    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      entities = entities.filter((e) =>
        e.name.toLowerCase().includes(lowerSearch),
      );
    }

    // Sort: tables first, then views, alphabetically within each group
    return entities.sort((a, b) => {
      if (a.type !== b.type) {
        return a.type === 'table' ? -1 : 1;
      }
      return a.name.localeCompare(b.name);
    });
  }, [metadata, searchTerm]);

  const selectedEntity = metadata?.entities.find((e) => e.name === value);

  if (error) {
    return (
      <Alert severity="error">
        Failed to load available tables/views. Please check your datastore
        connection.
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <Box display="flex" alignItems="center" gap={1} py={2}>
        <CircularProgress size={20} />
        <Typography variant="body2" color="text.secondary">
          Loading available tables and views...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Autocomplete
        value={selectedEntity || null}
        onChange={(event, newValue) => {
          onChange(newValue?.name || '', newValue || undefined);
        }}
        options={filteredEntities}
        getOptionLabel={(option) => option.name}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            required={required}
            placeholder="Search tables and views..."
          />
        )}
        renderOption={(props, option) => (
          <li {...props}>
            <Box display="flex" alignItems="center" gap={1} width="100%">
              <Chip
                label={option.type}
                size="small"
                color={option.type === 'table' ? 'primary' : 'default'}
                variant="outlined"
              />
              <Box flex={1}>
                <Typography variant="body2">{option.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {option.properties.length} fields
                </Typography>
              </Box>
            </Box>
          </li>
        )}
        filterOptions={(options, state) => {
          // Filter is handled by our useMemo, just return all options
          return options;
        }}
        onInputChange={(event, newInputValue) => {
          setSearchTerm(newInputValue);
        }}
        isOptionEqualToValue={(option, value) => option.name === value.name}
      />

      {/* Show selected entity details */}
      {selectedEntity && (
        <Box mt={2} p={2} bgcolor="background.default" borderRadius={1}>
          <Typography variant="subtitle2" gutterBottom>
            Selected: {selectedEntity.name}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            Type: {selectedEntity.type}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            Fields: {selectedEntity.properties.map((p) => p.name).join(', ')}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
